import React, { useEffect, useMemo, useState } from 'react';
import { FaTimes } from 'react-icons/fa';
import { useI18n } from '../../i18n';
import DatePickerInput from '../Common/DatePickerInput';
import dropdownChevronIcon from '../../assets/dropdown-chevron.svg';
import '../Projects/ProjectComposer.css';
import './RecurrenceModal.css';

const DAYS_OF_WEEK = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DEFAULT_WEEK_DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DAY_INITIALS = {
    Sun: 'S',
    Mon: 'M',
    Tue: 'T',
    Wed: 'W',
    Thu: 'T',
    Fri: 'F',
    Sat: 'S',
};

const getRepeatUnit = (recurrence = {}) => {
    const { frequency, interval } = recurrence;

    if (frequency === 'daily') return 'day';
    if (frequency === 'weekly' || frequency === 'custom') return 'week';
    if (frequency === 'monthly') {
        return interval && interval % 12 === 0 ? 'year' : 'month';
    }

    return 'week';
};

const getDisplayInterval = (recurrence = {}) => {
    const { frequency, interval } = recurrence;

    if (frequency === 'monthly' && interval && interval % 12 === 0) {
        return Math.max(interval / 12, 1);
    }

    return interval || 1;
};

const buildInitialFormData = (initialData = {}) => {
    const hasConfiguredRecurrence = initialData?.frequency && initialData.frequency !== 'once';

    if (!hasConfiguredRecurrence) {
        return {
            start_date: initialData?.start_date || null,
            recurrence_end_date: initialData?.recurrence_end_date || initialData?.end_date || null,
            interval: 1,
            days_of_week: [...DEFAULT_WEEK_DAYS],
        };
    }

    return {
        start_date: initialData.start_date || null,
        recurrence_end_date: initialData.recurrence_end_date || initialData.end_date || null,
        interval: getDisplayInterval(initialData),
        days_of_week: initialData.days_of_week?.length ? initialData.days_of_week : [...DEFAULT_WEEK_DAYS],
    };
};

const RecurrenceModal = ({ isOpen, onClose, onSave, initialData }) => {
    const { t } = useI18n();
    const [repeatUnit, setRepeatUnit] = useState('week');
    const [formData, setFormData] = useState(buildInitialFormData());

    useEffect(() => {
        if (!isOpen) return;

        setRepeatUnit(getRepeatUnit(initialData));
        setFormData(buildInitialFormData(initialData));
    }, [initialData, isOpen]);

    const handleStartDateChange = (date) => {
        setFormData(prev => ({ ...prev, start_date: date }));
    };

    const handleEndDateChange = (date) => {
        setFormData(prev => ({ ...prev, recurrence_end_date: date }));
    };

    const handleIntervalChange = (e) => {
        const nextValue = e.target.value;
        setFormData(prev => ({
            ...prev,
            interval: nextValue === '' ? '' : Math.max(1, Number(nextValue))
        }));
    };

    const handleRepeatUnitChange = (e) => {
        const nextUnit = e.target.value;
        setRepeatUnit(nextUnit);

        if (formData.days_of_week.length === 0) {
            setFormData(prev => ({
                ...prev,
                days_of_week: [...DEFAULT_WEEK_DAYS],
            }));
        }
    };

    const handleDayToggle = (day) => {
        if (repeatUnit === 'day') return;

        setFormData(prev => ({
            ...prev,
            days_of_week: prev.days_of_week.includes(day)
                ? prev.days_of_week.filter(d => d !== day)
                : [...prev.days_of_week, day]
        }));
    };

    const isFormValid =
        !!formData.start_date &&
        !!formData.recurrence_end_date &&
        (repeatUnit === 'day' || formData.days_of_week.length > 0);

    const handleSave = () => {
        const interval = Math.max(Number(formData.interval) || 1, 1);
        const selectedDays = formData.days_of_week.length > 0 ? formData.days_of_week : [...DEFAULT_WEEK_DAYS];

        const recurrence = {
            start_date: formData.start_date || null,
            recurrence_end_date: formData.recurrence_end_date || null,
            interval: repeatUnit === 'year' ? interval * 12 : interval,
            frequency: repeatUnit === 'day'
                ? 'daily'
                : repeatUnit === 'week'
                    ? 'weekly'
                    : 'monthly',
            days_of_week: repeatUnit === 'day' ? [] : selectedDays,
        };

        onSave(recurrence);
        onClose();
    };

    const DAY_I18N_KEYS = {
        Sun: 'pages.calendar.weekdays.sun',
        Mon: 'pages.calendar.weekdays.mon',
        Tue: 'pages.calendar.weekdays.tue',
        Wed: 'pages.calendar.weekdays.wed',
        Thu: 'pages.calendar.weekdays.thu',
        Fri: 'pages.calendar.weekdays.fri',
        Sat: 'pages.calendar.weekdays.sat',
    };

    const recurrenceText = useMemo(() => {
        const interval = Math.max(Number(formData.interval) || 1, 1);

        if (repeatUnit === 'week') {
            const orderedDays = DAYS_OF_WEEK.filter(day => formData.days_of_week.includes(day));

            if (orderedDays.length === 0) {
                return t('pages.calendar.schedule.occurs_every_week') || 'Occurs every week.';
            }

            if (orderedDays.length === 7) {
                return `${t('pages.calendar.schedule.occurs_every') || 'Occurs every'} ${t('pages.calendar.schedule.day_unit') || 'Day'}.`;
            }

            const translatedDays = orderedDays.map(day => t(DAY_I18N_KEYS[day]) || day);
            return `${t('pages.calendar.schedule.occurs_every') || 'Occurs every'} ${translatedDays.join(', ')}.`;
        }

        const unitLabels = {
            day: t('pages.calendar.schedule.day_unit') || 'Day',
            week: t('pages.calendar.schedule.week_unit') || 'Week',
            month: t('pages.calendar.schedule.month_unit') || 'Month',
            year: t('pages.calendar.schedule.year_unit') || 'Year',
        };

        const unitLabel = unitLabels[repeatUnit] || 'Week';
        const suffix = interval > 1 ? 's' : '';
        return `${t('pages.calendar.schedule.occurs_every') || 'Occurs every'} ${interval} ${unitLabel}${suffix}.`;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData.days_of_week, formData.interval, repeatUnit, t]);

    if (!isOpen) return null;

    return (
        <div
            className="recurrence-modal-overlay"
            onClick={(e) => {
                if (e.target === e.currentTarget) onClose();
            }}
        >
            <div className="project-composer-modal recurrence-modal-container">
                <div className="project-composer-header">
                    <h2 className="project-composer-title">
                        {t('pages.calendar.schedule.set_reoccurrence') || 'Set Reoccurrence'}
                    </h2>
                    <button type="button" className="project-composer-close" onClick={onClose}>
                        <FaTimes size={16} />
                    </button>
                </div>

                <div className="project-composer-body recurrence-modal-body">
                    <div className="recurrence-date-row">
                        <div className="project-form-group recurrence-form-group">
                            <label className="project-form-label">
                                {`${t('pages.calendar.schedule.start_date') || 'Start Date'}:`}
                            </label>
                            <DatePickerInput
                                selected={formData.start_date}
                                onChange={handleStartDateChange}
                                placeholder={t('pages.calendar.schedule.start_date_placeholder') || 'Select date'}
                            />
                        </div>

                        <div className="project-form-group recurrence-form-group">
                            <label className="project-form-label">
                                {`${t('pages.calendar.schedule.end_date') || 'End Date'}:`}
                            </label>
                            <DatePickerInput
                                selected={formData.recurrence_end_date}
                                onChange={handleEndDateChange}
                                placeholder={t('pages.calendar.schedule.end_date_placeholder') || 'Select date'}
                            />
                        </div>
                    </div>

                    <div className="recurrence-repeat-row">
                        <label className="project-form-label">
                            {`${t('pages.calendar.schedule.repeat_every') || 'Repeat Every'}:`}
                        </label>
                        <div className="recurrence-repeat-controls">
                            <input
                                type="number"
                                min="1"
                                max="99"
                                value={formData.interval}
                                onChange={handleIntervalChange}
                                className="project-form-input recurrence-number-input"
                            />

                            <select
                                value={repeatUnit}
                                onChange={handleRepeatUnitChange}
                                className="project-form-select recurrence-unit-select"
                                style={{ backgroundImage: `url("${dropdownChevronIcon}")` }}
                            >
                                <option value="day">{t('pages.calendar.schedule.day_unit') || 'Day'}</option>
                                <option value="week">{t('pages.calendar.schedule.week_unit') || 'Week'}</option>
                                <option value="month">{t('pages.calendar.schedule.month_unit') || 'Month'}</option>
                                <option value="year">{t('pages.calendar.schedule.year_unit') || 'Year'}</option>
                            </select>
                        </div>
                    </div>

                    <div className="recurrence-days-row">
                        <div className="recurrence-days-buttons">
                            {DAYS_OF_WEEK.map((day) => (
                                <button
                                    key={day}
                                    type="button"
                                    className={`day-button ${repeatUnit !== 'day' && formData.days_of_week.includes(day) ? 'active' : ''} ${repeatUnit === 'day' ? 'disabled' : ''}`}
                                    onClick={() => handleDayToggle(day)}
                                >
                                    {DAY_INITIALS[day]}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="project-composer-footer">
                    <button
                        type="button"
                        className="project-composer-btn project-composer-btn-secondary"
                        onClick={onClose}
                    >
                        {t('common.actions.cancel') || 'Cancel'}
                    </button>
                    <button
                        type="button"
                        className="project-composer-btn project-composer-btn-primary"
                        onClick={handleSave}
                        disabled={!isFormValid}
                    >
                        {t('common.actions.save') || 'Save'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default RecurrenceModal;
